import { useState, useCallback } from 'react';

export type ImageRemovalStatus = 'idle' | 'uploading' | 'processing' | 'completed' | 'failed';

export interface ImageRemovalMetadata {
  tier?: string;
  model?: string;
  durationMs?: number;
  degraded?: boolean;
  estimatedCredits?: number;
}

export function useImageRemoval() {
  const [status, setStatus] = useState<ImageRemovalStatus>('idle');
  const [progress, setProgress] = useState(0);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [resultUrl, setResultUrl] = useState<string | null>(null);
  const [metadata, setMetadata] = useState<ImageRemovalMetadata | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reset = useCallback(() => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    // Note: We don't revoke resultUrl as it comes from the server and we might want to keep it cached,
    // or if we do, it's a remote URL not an object URL anyway.
    setStatus('idle');
    setProgress(0);
    setPreviewUrl(null);
    setResultUrl(null);
    setMetadata(null);
    setError(null);
  }, [previewUrl]);

  const removeBackground = useCallback(
    async (file: File, options?: { model?: string; preferTier?: 'tier1-mps' | 'tier2-cpu' }) => {
      reset();

      if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) {
        setError('Unsupported file type. Please upload a PNG, JPEG, or WEBP image.');
        setStatus('failed');
        return;
      }

      const objectUrl = URL.createObjectURL(file);
      setPreviewUrl(objectUrl);
      setStatus('uploading');
      setProgress(10); // Fake initial progress

      try {
        const formData = new FormData();
        formData.append('image', file);

        const queryParams = new URLSearchParams();
        if (options?.model) queryParams.append('model', options.model);
        if (options?.preferTier) queryParams.append('preferTier', options.preferTier);

        const response = await fetch(`/api/v1/image/remove-background?${queryParams.toString()}`, {
          method: 'POST',
          body: formData, // the browser will set multipart/form-data boundary automatically
        });

        setStatus('processing');
        setProgress(50); // Fake processing progress

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || 'Failed to remove background');
        }

        if (data.status === 'failed') {
          throw new Error(data.error || 'Job failed upstream');
        }

        setResultUrl(data.imageUrl);
        setMetadata({
          tier: data.tier,
          model: data.metadata?.model,
          durationMs: data.durationMs,
          degraded: data.metadata?.degraded,
          estimatedCredits: data.creditsEarned,
        });
        setStatus('completed');
        setProgress(100);
      } catch (err: any) {
        setError(err.message || 'An unexpected error occurred');
        setStatus('failed');
        setProgress(0);
      }
    },
    [reset]
  );

  return {
    status,
    progress,
    previewUrl,
    resultUrl,
    metadata,
    error,
    removeBackground,
    reset,
  };
}
